import type {
  AvailabilityData,
  AvailabilitiesData,
  AvailabilitiesResponse,
  TimeSlotAvailabilityMap,
} from "@/types/api/availabilities";
import { fetchMarketplaceProductAvailabilities } from "@/api/services/marketplace-detail";
import type {
  ProductAvailabilitiesResponse,
  ProductCancellationPolicy,
  ProductDateEntry,
  ProductDetailsApiData,
  ProductDetailsApiPricingCategory,
  ProductDetailsApiStartTime,
  ProductDetailsData,
  ProductDetailsResponse,
  ProductPhoto,
  ProductPricingCategory,
  ProductReviews,
  ProductStartTime,
  ProductVideo,
} from "@/types/api/marketplace-product";
import type { MarketplaceReviewsData } from "@/types/api/marketplace-reviews";
import { computeWeightedCompanyRating } from "@/lib/operator/operator-ratings";
import { groupItineraryDaysByDayNumber } from "@/lib/utils";
import type {
  ActivityDetailsData,
  ActivityItineraryDay,
  ActivityPricingCategory,
  ActivityVideo,
  TripadvisorReview,
} from "@/types/shared/activity";

const DATE_KEY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const TIME_KEY_PATTERN = /^\d{1,2}:\d{2}(:\d{2})?$/;

function normalizeDateEntries(
  dates: Array<ProductDateEntry | string> | undefined
): string[] {
  if (!dates?.length) return [];

  return dates.map((entry) => (typeof entry === "string" ? entry : entry.date));
}

function normalizePricingCategories(
  categories: ProductDetailsApiPricingCategory[] | ProductPricingCategory[]
): ProductPricingCategory[] {
  return categories.map((category) => {
    if ("product_pricing_category_id" in category) {
      return {
        id: category.product_pricing_category_id,
        title: category.title,
        minAge: category.min_age,
        maxAge: category.max_age,
        isDefault: category.is_default,
      };
    }

    return category;
  });
}

function parseStartTimeValue(
  startTime: string
): Pick<ProductStartTime, "hours" | "minutes"> {
  const [hours = "0", minutes = "0"] = startTime.split(":");
  return {
    hours: Number(hours),
    minutes: Number(minutes),
  };
}

function normalizeStartTimes(
  startTimes: ProductDetailsApiStartTime[] | ProductStartTime[]
): ProductStartTime[] {
  return startTimes.map((startTime) => {
    if ("start_time" in startTime) {
      const { hours, minutes } = parseStartTimeValue(startTime.start_time);
      return {
        id: startTime.id,
        hours,
        minutes,
      };
    }

    return startTime;
  });
}

function normalizeCancellationPolicies(
  policies: string[] | ProductCancellationPolicy[] | undefined
): string[] {
  if (!policies?.length) {
    return [];
  }

  return policies
    .map((policy) =>
      typeof policy === "string" ? policy : policy.name?.trim() ?? ""
    )
    .filter(Boolean);
}

/**
 * Product details may return `reviews: []` when there are no reviews,
 * or a ProductReviews object when there are.
 */
function normalizeProductReviews(
  reviews: ProductReviews | unknown
): ProductReviews | undefined {
  if (!reviews || Array.isArray(reviews) || typeof reviews !== "object") {
    return undefined;
  }

  const data = reviews as ProductReviews;
  const items = data.reviews ?? [];
  const count = data.user_review_count ?? items.length;
  if (count <= 0 || items.length === 0) {
    return undefined;
  }

  return data;
}

/** Normalizes raw v2 product details API fields to app-friendly shape. */
export function normalizeProductDetailsData(
  raw: ProductDetailsApiData
): ProductDetailsData {
  const pricingCategories = normalizePricingCategories(raw.pricingCategories);
  const startTimes = normalizeStartTimes(raw.startTimes);
  const blackoutDates = normalizeDateEntries(raw.blackoutDates);
  const closeoutDates = normalizeDateEntries(raw.closeoutDates);
  const cancellationPolicies = normalizeCancellationPolicies(
    raw.cancellationPolicies
  );

  return {
    ...raw,
    pricingCategories,
    startTimes,
    blackoutDates,
    closeoutDates,
    cancellationPolicies,
    reviews: normalizeProductReviews(raw.reviews),
  };
}

/** Unwraps v2 product details response and normalizes payload. */
export function parseProductDetailsResponse(
  response: ProductDetailsResponse
): ProductDetailsData {
  return normalizeProductDetailsData(response.data);
}

function isDateAvailabilityEntry(
  key: string,
  value: unknown
): value is Record<string, unknown> {
  return (
    DATE_KEY_PATTERN.test(key) && typeof value === "object" && value !== null
  );
}

function isTimeKeyedSlotMap(value: Record<string, unknown>): boolean {
  return Object.keys(value).some((key) => TIME_KEY_PATTERN.test(key));
}

function isDirectAvailabilitySlot(value: unknown): value is AvailabilityData {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }

  const record = value as Record<string, unknown>;
  return (
    typeof record.date === "string" &&
    (record.rates !== undefined ||
      record.unlimitedAvailability !== undefined ||
      record.availabilityCount !== undefined)
  );
}

/**
 * Product API returns two shapes:
 * - `is_start_time_available: true` → { "2026-06-16": { "12:00:00": { slot } } }
 * - `is_start_time_available: false` → { "2026-06-12": { slot fields directly } }
 */
function normalizeTimeSlotMap(
  dateKey: string,
  value: Record<string, unknown>
): TimeSlotAvailabilityMap {
  if (isTimeKeyedSlotMap(value)) {
    return value as TimeSlotAvailabilityMap;
  }

  if (isDirectAvailabilitySlot(value)) {
    const slot = value as AvailabilityData;
    const timeKey = slot.startTime?.trim() || "";

    return {
      [timeKey]: {
        ...slot,
        date: slot.date || dateKey,
        startTime: slot.startTime?.trim() || "",
        startTimeId: slot.startTimeId ?? 0,
        pickupAvailabilityCount: slot.pickupAvailabilityCount ?? 0,
        availabilityCount: slot.availabilityCount ?? 0,
      },
    };
  }

  return value as TimeSlotAvailabilityMap;
}

function extractAvailabilityEntries(
  rawData: Record<string, unknown>
): AvailabilitiesData {
  const availabilityData: AvailabilitiesData = {};

  for (const [key, value] of Object.entries(rawData)) {
    if (isDateAvailabilityEntry(key, value)) {
      availabilityData[key] = normalizeTimeSlotMap(key, value);
    }
  }

  return availabilityData;
}

const EMPTY_AVAILABILITIES_RESPONSE: AvailabilitiesResponse = {
  message: "",
  error: false,
  status_code: 200,
  data: {},
};

/** Normalizes v2 product availabilities API payload to AvailabilitiesResponse. */
export function mapProductAvailabilitiesResponse(
  response: ProductAvailabilitiesResponse
): AvailabilitiesResponse {
  const availabilityData = extractAvailabilityEntries(response.data);

  return {
    message: response.message,
    error: response.error,
    status_code: response.status_code,
    data: availabilityData,
  };
}

/** Fetches availabilities without failing the product detail page. */
export async function fetchMarketplaceProductAvailabilitiesSafe(
  productUid: string,
  start: string,
  end: string
): Promise<AvailabilitiesResponse> {
  try {
    const raw = await fetchMarketplaceProductAvailabilities(
      productUid,
      start,
      end
    );
    return mapProductAvailabilitiesResponse(raw);
  } catch (error) {
    console.error(
      `[marketplace] Failed to fetch product availabilities for ${productUid}:`,
      error
    );
    return EMPTY_AVAILABILITIES_RESPONSE;
  }
}

function orderPhotosWithCoverFirst(photos: ProductPhoto[]): ProductPhoto[] {
  const coverPhoto = photos.find((photo) => photo.is_cover);
  if (!coverPhoto) return photos;

  return [coverPhoto, ...photos.filter((photo) => photo !== coverPhoto)];
}

function formatDurationText(item: ProductDetailsData): string {
  if (item.durationDays > 0) {
    return `${item.durationDays} Day${item.durationDays > 1 ? "s" : ""}`;
  }
  if (item.durationHours > 0) {
    return `${item.durationHours} Hour${item.durationHours > 1 ? "s" : ""}`;
  }
  if (item.durationMinutes > 0) {
    return `${item.durationMinutes} Minutes`;
  }
  return "";
}

function listToHtml(items: string[]): string {
  if (items.length === 0) return "";
  return `<ul>${items.map((item) => `<li>${item}</li>`).join("")}</ul>`;
}

function getDefaultRate(product: ProductDetailsData) {
  return (
    product.rates.find(
      (rate) => String(rate.id) === String(product.defaultRateId)
    ) ??
    product.rates.find((rate) => rate.is_default) ??
    product.rates[0]
  );
}

function getDefaultPricingCategoryId(product: ProductDetailsData): number {
  const defaultCategory =
    product.pricingCategories.find((category) => category.isDefault) ??
    product.pricingCategories[0];

  if (defaultCategory) {
    return defaultCategory.id;
  }

  const defaultRate = getDefaultRate(product);
  const firstCategoryPrice = defaultRate?.prices.find(
    (price) => price.product_pricing_category_id != null
  );

  return firstCategoryPrice?.product_pricing_category_id ?? 1;
}

function getDetailsPrice(product: ProductDetailsData) {
  const defaultRate = getDefaultRate(product);
  const defaultCategoryId = getDefaultPricingCategoryId(product);
  const categoryPrice = defaultRate?.prices.find(
    (price) => price.product_pricing_category_id === defaultCategoryId
  );
  const firstCategoryPrice = defaultRate?.prices.find(
    (price) => price.product_pricing_category_id != null
  );

  const amount =
    categoryPrice?.amount ??
    firstCategoryPrice?.amount ??
    product.price.value ??
    0;
  const currency = product.price.currency ?? product.currencyCode ?? "INR";

  return { amount, currency };
}

function getProductItineraryPlaceName(
  pointsOfInterest: ProductDetailsData["itinerary"][number]["points_of_interest"]
): string | undefined {
  const names = pointsOfInterest
    .map((poi) => poi.label?.trim() || poi.location?.name?.trim())
    .filter((name): name is string => Boolean(name));

  if (names.length === 0) return undefined;
  return [...new Set(names)].join(", ");
}

function mapProductItinerary(
  itinerary: ProductDetailsData["itinerary"]
): ActivityItineraryDay[] {
  const sorted = [...itinerary].sort(
    (a, b) => a.sequence_order - b.sequence_order
  );

  const mapped = sorted.map((day) => {
    const firstPoi = day.points_of_interest[0];
    const placeName = getProductItineraryPlaceName(day.points_of_interest);

    return {
      day: day.day_number,
      items: [
        {
          title: day.title,
          description: day.description,
          html_body: `<p>${day.description}</p>`,
          placeName,
          location: {
            countryCode: firstPoi?.location?.countryCode ?? null,
            city: firstPoi?.location?.city ?? null,
            latitude: firstPoi?.location?.lat ?? null,
            longitude: firstPoi?.location?.lng ?? null,
          },
        },
      ],
    };
  });

  return groupItineraryDaysByDayNumber(mapped);
}

function mapPricingCategoriesFromProduct(
  product: ProductDetailsData
): ActivityPricingCategory[] {
  return product.pricingCategories.map((category) => ({
    id: category.id,
    title: category.title,
    minAge: category.minAge ?? 0,
    maxAge: category.maxAge ?? 99,
    isDefault: category.isDefault ?? false,
  }));
}

function mapProductVideos(videos: ProductVideo[] | undefined): ActivityVideo[] {
  if (!videos?.length) return [];

  return [...videos]
    .sort((a, b) => a.sequence_order - b.sequence_order)
    .map((video) => {
      const sourceUrl = video.url?.trim() ?? "";
      if (!sourceUrl) return null;

      return {
        sourceUrl,
        providerName: video.title?.trim() || "Video",
        html: "",
      };
    })
    .filter((video): video is ActivityVideo => video !== null);
}

function formatReviewMonthYear(reviewedAt: string | null | undefined): {
  month: string;
  year: number;
} {
  if (!reviewedAt) {
    return { month: "", year: 0 };
  }
  const date = new Date(reviewedAt);
  if (Number.isNaN(date.getTime())) {
    return { month: "", year: 0 };
  }

  return {
    month: date.toLocaleString("en-US", { month: "long" }),
    year: date.getFullYear(),
  };
}

/** Maps product details reviews API payload to marketplace reviews UI model. */
export function mapProductReviewsToMarketplaceReviewsData(
  reviews: ProductReviews | undefined
): MarketplaceReviewsData | null {
  const normalized = normalizeProductReviews(reviews);
  if (!normalized) {
    return null;
  }

  return {
    reviewCount: normalized.user_review_count,
    recommendedCount: normalized.user_recommended_count,
    recommendedPercentage: normalized.user_recommended_percentage,
    reviews: (normalized.reviews ?? []).map((review, index) => {
      const { month, year } = formatReviewMonthYear(review.reviewed_at);
      const hasRecommended = review.has_recommended;
      return {
        id: `review-${index}-${review.reviews_name}-${review.reviewed_at}`,
        reviewerName: review.reviews_name || "Traveler",
        month,
        year,
        hasRecommended,
        rating:
          typeof review.rating === "number"
            ? review.rating
            : hasRecommended
              ? 5
              : 1,
        comment: review.comment ?? "",
        photos: review.images?.filter(Boolean),
      };
    }),
  };
}

export type MarketplaceHeaderRatingMode =
  | "operatorWeighted"
  | "recommendedCount"
  | "recommendedPercent";

export interface MarketplaceHeaderRating {
  mode: MarketplaceHeaderRatingMode;
  /** Star score — only for operatorWeighted */
  rating?: number;
  reviewCount: number;
  recommendedCount?: number;
  recommendedPercentage?: number;
}

/**
 * Builds experience header rating display from **this product’s** review count:
 * <5  → company weighted stars + operator reviews
 * 5–39 → "N Recommended (Y reviews)"
 * ≥40 → "P% Recommended (Y reviews)"
 *
 * Threshold count always comes from product `reviews` only.
 * Weighted rating blends Google + Lykke from product root or nested operator.
 */
export function mapProductDetailsToHeaderRating(
  product: ProductDetailsData
): MarketplaceHeaderRating | null {
  const googleRating =
    product.googleRating ?? product.operator?.googleRating ?? null;
  const companyLykkeRating =
    product.operator?.travelLykkeRating ??
    product.travelLykkeRating ??
    null;

  const reviewCount = product.reviews
    ? (product.reviews.user_review_count ??
      product.reviews.reviews?.length ??
      0)
    : 0;

  if (reviewCount < 5) {
    const weighted = computeWeightedCompanyRating(
      googleRating,
      companyLykkeRating
    );
    if (!weighted) {
      return null;
    }
    return {
      mode: "operatorWeighted",
      rating: weighted.rating,
      reviewCount: weighted.reviewCount,
    };
  }

  const recommendedCount = product.reviews?.user_recommended_count ?? 0;
  const recommendedPercentage =
    product.reviews?.user_recommended_percentage ?? 0;

  if (reviewCount < 40) {
    return {
      mode: "recommendedCount",
      reviewCount,
      recommendedCount,
    };
  }

  return {
    mode: "recommendedPercent",
    reviewCount,
    recommendedPercentage,
  };
}

function mapProductReviewsToTripadvisorReview(
  reviews: ProductReviews | undefined
): TripadvisorReview {
  const normalized = normalizeProductReviews(reviews);
  if (!normalized) {
    return {};
  }

  return {
    rating: (normalized.user_recommended_percentage * 5) / 100,
    numReviews: normalized.user_review_count,
  };
}

/** Maps normalized marketplace product details to shared activity detail shape. */
export function mapProductDetailsToActivityDetails(
  product: ProductDetailsData
): ActivityDetailsData {
  const { amount, currency } = getDetailsPrice(product);
  const pricingCategories = mapPricingCategoriesFromProduct(product);

  return {
    uid: product.uid,
    inventoryType: "PRODUCT",
    activityInventoryId: product.uid,
    slug: product.slug,
    activityType: product.productCategories[0] ?? "ACTIVITY",
    isPrivate: false,
    isB2BEnabled: false,
    isB2CEnabled: true,
    b2bDiscountPercentage: 0,
    title: product.title,
    shortDescription: product.shortDescription,
    description: product.description,
    activityCategories: product.productCategories,
    activityAttributes: product.keywords,
    difficultyLevel: "EASY",
    photos: orderPhotosWithCoverFirst(product.photos)
      .map((photo) => photo.url?.trim() ?? "")
      .filter(Boolean),
    videos: mapProductVideos(product.videos),
    country: product.country,
    countryCode: product.countryCode,
    city: product.city,
    cityCode: product.countryCode,
    location: {
      name: product.location.name,
      country: product.location.country,
      countryCode: product.location.countryCode,
      city: product.location.city,
      cityCode: product.location.countryCode,
      latitude: product.location.lat,
      longitude: product.location.lng,
    },
    tripadvisorReview: mapProductReviewsToTripadvisorReview(product.reviews),
    vendorName: product.operator.name,
    vendorSlug: product.operator.slug,
    bookingType: product.bookingType,
    scheduleType: product.bookingType,
    capacityType: product.capacityType,
    passCapacity: 0,
    passValidForDays: 0,
    passesAvailable: 0,
    dressCode: false,
    passportRequired: false,
    supportedAccessibilityTypes: [],
    meetingType: "MEETING_POINT",
    startPoints: [],
    pickupService: false,
    dropoffService: false,
    bookingCutoffMinutes: product.bookingCutoffMinutes,
    bookingCutoffHours: product.bookingCutoffHours,
    bookingCutoffDays: product.bookingCutoffDays,
    bookingCutoffWeeks: product.bookingCutoffWeeks,
    reservationTimeout: product.reservationTimeout,
    minimumAge: product.minimumAge,
    inclusions: product.inclusions,
    exclusions: product.exclusions,
    inclusionHtml: listToHtml(product.inclusions),
    exclusionHtml: listToHtml(product.exclusions),
    requirementsHtml: listToHtml(product.whatToBring.map((item) => item.label)),
    knowBeforeYouGoItems: product.knowBeforeYouGo.map((item) => item.text),
    attentionHtml: "",
    itinerary: mapProductItinerary(product.itinerary),
    defaultRateId: String(product.defaultRateId),
    durationType: product.durationType,
    durationMinutes: product.durationMinutes,
    durationHours: product.durationHours,
    durationDays: product.durationDays,
    durationWeeks: product.durationWeeks,
    duration: product.duration,
    durationText: formatDurationText(product),
    price: { amount, currency },
    pricingCategories,
    startTimes: product.startTimes.map((startTime) => ({
      id: startTime.id,
      hours: startTime.hours,
      minutes: startTime.minutes,
    })),
    extras: [],
    defaultOpeningHours: {
      monday: { isOpen24Hours: false, timeIntervals: [] },
      tuesday: { isOpen24Hours: false, timeIntervals: [] },
      wednesday: { isOpen24Hours: false, timeIntervals: [] },
      thursday: { isOpen24Hours: false, timeIntervals: [] },
      friday: { isOpen24Hours: false, timeIntervals: [] },
      saturday: { isOpen24Hours: false, timeIntervals: [] },
      sunday: { isOpen24Hours: false, timeIntervals: [] },
    },
    seasonalOpeningHours: [],
    cancellationPolicies: product.cancellationPolicies,
    timeZone: product.timeZone,
    isActive: product.isPublished,
    salesContact: "",
    bookingHighlights: [],
    aboutThisTour: product.keywords.map((keyword) => ({
      icon: "check-square",
      label: keyword,
    })),
  };
}
